'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { Menu, X, User } from 'lucide-react'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuLink,
} from '@/components/ui/navigation-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/* ── Helper: shadcn ListItem ───────────────────────────────────────────── */
const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'> & { title: string }
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            className,
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          {children && (
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {children}
            </p>
          )}
        </a>
      </NavigationMenuLink>
    </li>
  )
})
ListItem.displayName = 'ListItem'

/* ── Top-level nav items ──────────────────────────────────────────────── */
const navItems = [
  { name: 'Services', hasDropdown: true, href: '/services' },
  { name: 'About Us', hasDropdown: false, href: '/about' },
  { name: 'Our Team', hasDropdown: false, href: '/team' },
  { name: 'Careers', hasDropdown: false, href: '/careers' },
  { name: 'Blog', hasDropdown: false, href: '/blog' },
]

/* ── Services submenu (added 2 new items) ─────────────────────────────── */
const servicesSubItems = [
  {
    title: 'Online Psychiatry',
    href: '/services/online-psychiatry',
    description: 'Virtual consultations with licensed psychiatrists.',
  },
  {
    title: 'Medication Management',
    href: '/services/medication-management',
    description: 'Evidence-based pharmacological treatment plans.',
  },
  {
    title: 'Individual Therapy',
    href: '/services/individual-therapy',
    description: 'One-on-one psychotherapy sessions tailored to you.',
  },
  {
    title: 'Psychiatric Evaluations',
    href: '/services/psychiatric-evaluation',
    description: 'Comprehensive diagnostic assessments.',
  },
  {
    title: 'Surrogacy Eval',
    href: '/services/surrogacy-eval',
    description: 'Specialized evaluations for surrogacy processes.',
  },
  {
    title: 'ADHD Coaching',
    href: '/attention-deficit-hyperactivity-disorder-therapy',
    description: 'ADHD services for adults.',
  },
  {
    title: 'Child & Adolescent ADHD',
    href: '/child-adhd',
    description: 'Evaluation and treatment for ADHD in children and teens.',
  },
  {
    title: 'Anxiety Services',
    href: '/anxiety-services',
    description: 'Assessment and evidence-based care for anxiety disorders.',
  },
  {
    title: 'Sports Psychology',
    href: '/services/sports-psychology',
    description: 'become a better sportsperson',
  },
]

/* ── Component ────────────────────────────────────────────────────────── */
const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = usePathname()
  const router = useRouter()

  const isActive = (href: string) => pathname.startsWith(href)
  const isServices = pathname.startsWith('/services')
  const isPatientPortal = pathname.startsWith('/patient')

  return (
    <div className="fixed top-12 left-1/2 z-50 w-[calc(100%-1rem)] max-w-[1275px] -translate-x-1/2 sm:top-14 sm:w-[calc(100%-2rem)]">
      <nav
        className="flex h-16 items-center justify-between pl-[41px] pr-8 py-2 relative bg-white/20 backdrop-blur-md border border-white/30 rounded-3xl shadow-lg"
        data-model-id="176:558"
      >
        <Link href="/" className="flex items-center">
          <Image
            src="/logos/default-horizontal-logo.png"
            alt="ReACH Psychiatry logo"
            width={149}
            height={35}
            priority
            className="relative w-[149px] h-[34.65px]"
          />
          <span className="sr-only">ReACH Psychiatry</span>
        </Link>

        <div className="hidden md:flex items-center">
          <NavigationMenu>
            <NavigationMenuList className="flex items-center gap-4">
              {navItems.map((item) => (
                <NavigationMenuItem key={item.name}>
                  {item.hasDropdown ? (
                    <>
                      <NavigationMenuTrigger
                        onClick={() => router.push('/services')}
                        className={cn(
                          'font-text-sm-medium … hover:bg-white/20 transition-transform duration-200',
                          'border-b-2 border-transparent',
                          isServices && 'border-zinc-800 font-normal',
                        )}
                      >
                        {item.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid gap-4 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-2 bg-white/90 backdrop-blur-md border border-white/30 rounded-lg shadow-lg">
                          {servicesSubItems.map((si) => (
                            <ListItem
                              key={si.title}
                              title={si.title}
                              href={si.href}
                            >
                              {si.description}
                            </ListItem>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <NavigationMenuLink asChild>
                      <Link
                        href={item.href}
                        className={cn(
                          'font-text-sm-medium … h-auto hover:bg-white/20 transition-transform duration-200 px-4 py-2',
                          'border-b-2 border-transparent',
                          isActive(item.href) && 'border-zinc-800 font-normal',
                        )}
                      >
                        {item.name}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Desktop: Patient Portal + Book a session */}
        <div className="hidden md:flex items-center gap-2">
          <Button
            asChild
            variant="ghost"
            className={cn(
              'h-9 px-3 rounded-full text-sm font-medium hover:bg-white/30 transition-colors',
              isPatientPortal && 'bg-white/30',
            )}
          >
            <Link href="/patient" className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              My Records
            </Link>
          </Button>
          <Button
            asChild
            className="h-9 px-6 rounded-full bg-cyan-400 hover:bg-cyan-500 text-black font-semibold shadow-md"
          >
            <Link href="/book">Book a session</Link>
          </Button>
        </div>

        <div className="md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-black"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="md:hidden mt-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-3xl shadow-lg p-4">
          <nav className="flex flex-col items-center gap-4">
            {navItems.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  'text-black hover:text-gray-600 w-full text-center py-2',
                  isActive(item.href) && 'font-bold',
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}

            {/* Patient Portal link in mobile menu */}
            <Link
              href="/patient"
              className={cn(
                'text-black hover:text-gray-600 w-full text-center py-2 flex items-center justify-center gap-1.5',
                isPatientPortal && 'font-bold',
              )}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <User className="w-4 h-4" />
              My Records
            </Link>

            <Button
              asChild
              className="w-full rounded-full bg-cyan-400 hover:bg-cyan-500 text-black font-semibold"
            >
              <Link href="/book" onClick={() => setIsMobileMenuOpen(false)}>
                Book a session
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </div>
  )
}

export default Header
